import { useCallback, useMemo, useState } from "react";
import { arrayMove } from "@dnd-kit/sortable";
import { OrganizationCloseStage } from "@common/types/organizationCloseStage";
import { View, ViewDensity, ViewType } from "@common/types/views/views";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { Loader } from "@design-system/Loader/Loader";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { ViewTableMeta } from "@hooks/view/useViewTableMeta";
import {
    useOrganizationCloseStages,
    useDeleteOrganizationCloseStageMutation,
    useChangeOrganizationCloseStagePositionMutation,
} from "@api/organizationCloseStage.api";
import { OrganizationCloseStageFormModal } from "./OrganizationCloseStageFormModal";
import {
    OrganizationCloseStageColumn,
    OrganizationCloseStageRow,
    organizationCloseStagesViewConfig,
} from "./organizationCloseStagesViewConfig";

interface Props {
    search: string;
    canManage: boolean;
    isExternalActionActive?: boolean;
}

interface OrganizationCloseStageCallbacks {
    onEditCloseStage: (closeStageId: string) => void;
    onDeleteCloseStage: (closeStageId: string) => void;
}

const defaultView: View = {
    type: ViewType.TASK_GROUP,
    columns: [
        { id: OrganizationCloseStageColumn.NAME },
        { id: OrganizationCloseStageColumn.UPDATED },
        {
            id: OrganizationCloseStageColumn.ACTIONS,
            options: { pin: "right" },
        },
    ],
    filters: {},
    options: {
        selection: false,
        bulkActions: false,
        density: ViewDensity.MD,
    },
};

const ROOT_GROUP_ID = "__root__";

export const OrganizationCloseStagesList = ({
    search,
    canManage,
    isExternalActionActive = false,
}: Props) => {
    const { error: errorToast } = useToaster();
    const { data: closeStages, isFetched } = useOrganizationCloseStages();

    const [editingCloseStage, setEditingCloseStage] =
        useState<OrganizationCloseStage | null>(null);
    const [deletingCloseStage, setDeletingCloseStage] =
        useState<OrganizationCloseStage | null>(null);

    const deleteCloseStageMutation = useDeleteOrganizationCloseStageMutation(
        deletingCloseStage ?? undefined,
    );
    const changeCloseStagePositionMutation =
        useChangeOrganizationCloseStagePositionMutation();

    const closeStagesList = useMemo(() => closeStages ?? [], [closeStages]);

    const closeStagesById = useMemo(
        () => new Map(closeStagesList.map((cs) => [cs.id, cs])),
        [closeStagesList],
    );

    const filteredRows = useMemo<OrganizationCloseStageRow[]>(() => {
        const lowerSearch = search.toLowerCase();

        return closeStagesList
            .filter(
                (cs) => !search || cs.name.toLowerCase().includes(lowerSearch),
            )
            .sort((a, b) => a.order - b.order)
            .map((cs) => ({
                id: cs.id,
                name: cs.name,
                updatedAt: cs.updatedAt,
                organizationId: cs.organizationId,
                parentCloseStageId: cs.parentCloseStageId,
                rootCloseStageId: cs.rootCloseStageId,
                order: cs.order,
            }));
    }, [closeStagesList, search]);

    const handleEditCloseStage = useCallback(
        (closeStageId: string) => {
            const cs = closeStagesById.get(closeStageId);
            if (cs) {
                setEditingCloseStage(cs);
            }
        },
        [closeStagesById],
    );

    const handleDeleteCloseStage = useCallback(
        (closeStageId: string) => {
            const cs = closeStagesById.get(closeStageId);
            if (cs) {
                setDeletingCloseStage(cs);
            }
        },
        [closeStagesById],
    );

    const tableMeta = useMemo<
        ViewTableMeta<OrganizationCloseStageCallbacks, never>
    >(
        () => ({
            callbacks: canManage
                ? {
                      onEditCloseStage: handleEditCloseStage,
                      onDeleteCloseStage: handleDeleteCloseStage,
                  }
                : undefined,
        }),
        [canManage, handleEditCloseStage, handleDeleteCloseStage],
    );

    const siblingIdsByGroup = useMemo(() => {
        const siblingMap = new Map<string, string[]>();
        siblingMap.set(
            ROOT_GROUP_ID,
            filteredRows.map((row) => row.id),
        );
        return siblingMap;
    }, [filteredRows]);

    const canDragRow = useCallback(
        () => canManage && !changeCloseStagePositionMutation.isPending,
        [canManage, changeCloseStagePositionMutation.isPending],
    );

    const getRowDragGroupId = useCallback(
        (_row: OrganizationCloseStageRow) => ROOT_GROUP_ID,
        [],
    );

    const handleRowDrop = useCallback(
        async ({
            activeRow,
            overRow,
        }: {
            activeRow: OrganizationCloseStageRow;
            overRow: OrganizationCloseStageRow;
        }) => {
            const activeGroupId = activeRow.parentCloseStageId ?? ROOT_GROUP_ID;
            const overGroupId = overRow.parentCloseStageId ?? ROOT_GROUP_ID;
            if (activeGroupId !== overGroupId) {
                return;
            }

            const siblingIds = siblingIdsByGroup.get(activeGroupId);
            if (!siblingIds || siblingIds.length <= 1) {
                return;
            }

            const sourceIndex = siblingIds.indexOf(activeRow.id);
            const targetIndex = siblingIds.indexOf(overRow.id);
            if (
                sourceIndex === -1 ||
                targetIndex === -1 ||
                sourceIndex === targetIndex
            ) {
                return;
            }

            const reorderedIds = arrayMove(
                siblingIds,
                sourceIndex,
                targetIndex,
            );
            const newOrder = reorderedIds.indexOf(activeRow.id);
            if (newOrder === sourceIndex) {
                return;
            }

            try {
                await changeCloseStagePositionMutation.mutateAsync({
                    closeStageId: activeRow.id,
                    parentCloseStageId: activeRow.parentCloseStageId,
                    order: newOrder,
                });
            } catch {
                errorToast("Failed to reorder stage");
            }
        },
        [changeCloseStagePositionMutation, errorToast, siblingIdsByGroup],
    );

    const isAnyActionActive =
        isExternalActionActive || !!editingCloseStage || !!deletingCloseStage;

    const rowDragAndDropConfig = useMemo(
        () => ({
            disabled: isAnyActionActive,
            canDragRow,
            getRowDragGroupId,
            onRowDrop: handleRowDrop,
            dragHandleColumnId: OrganizationCloseStageColumn.ACTIONS,
        }),
        [canDragRow, getRowDragGroupId, handleRowDrop, isAnyActionActive],
    );

    return (
        <>
            <ViewTable
                data={isFetched ? filteredRows : null}
                view={defaultView}
                config={organizationCloseStagesViewConfig}
                emptyView={() => <EmptyState header="No stages found" />}
                loadingView={() => <Loader />}
                tableMeta={tableMeta}
                enableColumnResizing={false}
                outerBorder
                autoExpandOnMount
                rowDragAndDrop={rowDragAndDropConfig}
            />

            <OrganizationCloseStageFormModal
                show={!!editingCloseStage}
                closeStage={editingCloseStage ?? undefined}
                onHide={() => setEditingCloseStage(null)}
            />

            <ConfirmationModal
                show={!!deletingCloseStage}
                title={`Delete ${deletingCloseStage?.name}?`}
                question="This stage and all its sub-stages will be permanently removed."
                yes="Delete"
                no="Cancel"
                onReject={() => setDeletingCloseStage(null)}
                onConfirm={async () => {
                    try {
                        await deleteCloseStageMutation.mutateAsync();
                        setDeletingCloseStage(null);
                    } catch {
                        errorToast("Failed to delete stage");
                    }
                }}
            />
        </>
    );
};
